// The landing page (Main.dc.html, chosen; U1, U2, U5, U6; B181): the ask bar across the top,
// the questions card above the ten while any remain (U18), "Your ten" numbered in rank
// order (Quick wins gone since REQ-2026-10-13-16), the awareness strip with "show all" (U4), private
// items in violet under the personal toggle (U3, D77), a returned item with what changed
// first (D66), and the interrupt banner for a clocked item inside the hour (D12, D46).
//
// With the toggle on, the page asks GET /api/triage?store= once per store the user has and
// merges the two lists by score in the app (D12, D43: the stores are never joined in the
// database). Every action posts one decision and the list changes in place, no reload.
// Back from an item returns to the same place in the ten (U12): the last list and the
// scroll position are kept for the session.
//
// The visual and interaction upgrade (module 10, 2026-09-19): the look is U28's "Match centre"
// (the score bug heads the ten; the cleared card wipes out, the ten closes up, the item from
// eleven arrives at ten); the page is keyboard-first (U33); a decision that takes the item out
// of the ten is held here for eight seconds before it is sent, so Undo sends nothing (U34);
// Complete and Dismiss are one press each on this page (U35). Nothing is sent by a key (U36).
//
// The call read (module 04 D288, D299; REQ-2026-09-24-09): above Your list, a line for each card a call he placed from it
// cleared in the last two days, with the summary's own words and one click to reopen; a card the call brought back with a
// step says the step first (ItemCard.tsx).
//
// The filter bar (module 10 U228; pipeline F6, B1956), drawn open under the score bug as on every list page: find over a
// card's headline, gist and who, and By company, By deal or matter, By channel and By kind, kept in the address. It
// hides cards and never moves the rest: the ten keep their numbers, a line says "Showing 3 of your ten, narrowed to
// Kambi", and J and K walk the cards shown. The whole pass is in the page already, so the narrowing is the browser's.

import { Chip, SectionHeading, UndoStrip } from './parts/primitives.js';
import { ActionButton } from './parts/ActionButton.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { foundReadingBack, ReadingBackStrip, useReadingBack } from './onboarding/ReadingBack.js';
import { useLiveRefresh } from './live.js';
import { ClearedHistory } from './cleared/ClearedHistory.js';
import { AskAnswer, AskBar, askStart, type AskState } from './AskBar.js';
import { triage, ApiError, Unauthenticated, type DecideBody, type Notification, type Option, type PassItem, type Store, type TeamMember, type DecideResult } from './api.js';
import { ItemCard, whenText, type CardHandle } from './ItemCard.js';
import { count } from './labels.js';
import { QuestionsCard, type Questions } from './Questions.js';
import { HeldCallsCard } from './HeldCalls.js';
import { StoreQuestionCard } from './rails/StoreQuestion.js';
import { bannerBody, imminent, inWords, mergeStores, stacks, TEN, type Standing } from './ranking.js';
import { claimOf } from './claim.js';
import { ClaimLine } from './claim-parts.js';
import { CompanyBar, CompanyLines } from './intel/CompanyBar.js';
import { VersionPicker } from './version/VersionPicker.js';
import { clearedOf, reopenCallRead, type ClearedCard } from './call/read-api.js';
import { AllocateCard, useToAllocate, type AllocateSaid } from './playbooks/AllocateCard.js';
import { itemMatches } from './playbooks/book.js';
import { FilterBar, matchesWords, tally } from './parts/FilterBar.js';
import { useFilterParams } from './find/params.js';
import { kindWord } from './records/shared.js';
import { mergeStillOpen, recordVisit, sinceWords, stillOpenOf, type StillOpen, type StillOpenItem, type VisitVia } from './rotation.js';

/** What Triage is narrowed to, kept in the address (pipeline F6). */
const FIND = ['q', 'company', 'matter', 'channel', 'kind'] as const;
type Narrowing = Record<(typeof FIND)[number], string>;

/** "By kind": what a card is about, in the page's words (pipeline F6, B1956, taken without asking under launch rule 1). */
const KIND_WORDS: Record<string, string> = { commitment: 'Commitment', task: 'Task', deal: 'Deal', calendar_event: 'Meeting', news_item: 'News', signal: 'Message' };
export const kindOfCard = (k: string): string => KIND_WORDS[k] ?? (k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' '));

/** Whether a card is one the bar leaves showing. */
export function fitsNarrowing(it: PassItem, f: Narrowing): boolean {
  return matchesWords(f.q, it.headline, it.gist, it.who)
    && (!f.company || it.organisation?.id === f.company)
    && (!f.matter || it.matter?.id === f.matter)
    && (!f.channel || it.channels.some((c) => kindWord(c) === f.channel))
    && (!f.kind || it.subjectKind === f.kind);
}

const NUMBER_WORDS = ['none', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];
const inWordsN = (n: number): string => NUMBER_WORDS[n] ?? String(n);

/** The line over a narrowed ten: "Showing 3 of your ten, narrowed to Kambi". */
export function narrowedLine(shown: number, of: number, to: string[]): string {
  return `Showing ${shown} of your ${inWordsN(of)}, narrowed to ${to.join(', ')}`;
}

/** D254 (B1493): what the triage read says about the top of the page; absent from an older server, which means ten, unchosen. */
interface TopOfPass { top?: number; chosen?: boolean }
/** Module 04 D299 answer 1: a card a call cleared, with the store whose read listed it, which its Reopen posts to. */
export interface ClearedLine extends ClearedCard { store: string }
/** Module 04 B2220, BUGS.md T55: what is open and not on today's list, over the stores on the page, each item with its store. */
export type StillOpenView = Omit<StillOpen, 'items'> & { items: Array<StillOpenItem & { store: string }> };
interface Loaded { items: PassItem[]; team: Record<string, TeamMember[]>; ranked: boolean; at: number; top: number; chosen: boolean; cleared: ClearedLine[]; clearedToday: number | null;
  /** T55: the line under the list and the open list it opens; null when no store's pass carries it (a version without the rotation). */
  stillOpen: StillOpenView | null;
  /** T55: the pass each store's list was drawn from, which a visit names (migration 226). */
  passes: Array<{ store: string; passId: string | null }> }
type Load = { kind: 'loading' } | { kind: 'ready'; data: Loaded } | { kind: 'error'; message: string };

/** The last list per set of stores, so Back returns to the same place in the ten (U12). */
const cache = new Map<string, Loaded>();
const CACHE_MS = 10 * 60_000;
const SCROLL_KEY = 'cobrain.triage.scroll';
const HANDLED_KEY = 'cobrain.triage.handled';
/** What the item page opens with when a decision made a draft, or an option asked for one. */
export const DRAFT_KEY = 'cobrain.triage.draft';
/**
 * What the card hands the item page when a reply is asked for. `instruction` is the sentence he
 * typed in the box (module 04 D259 part 2), and it is carried here rather than dropped: without
 * it the box is a form whose answer goes nowhere, which is what the flow did until 2026-09-22.
 * `language` was already here, and the API has been discarding it since the route was written.
 */
export interface DraftIntent {
  itemId: string;
  store: string;
  draftId: string | null;
  purpose: 'reply' | 'chase' | 'acknowledge' | 'invite' | 'note';
  language: string | null;
  instruction: string | null;
  /**
   * The option pressed (actions contract row 1, REQ-2026-09-24-05): the draft panel's Send hands it to the API, which
   * records the card's decision only once the draft has gone. Absent on an intent written before this field.
   */
  optionKey?: string | null;
}

/** U34: how long a decision is held before it is sent. */
export const HOLD_MS = 8_000;
/** The wipe's length (VISUAL-SYSTEM, Motion); the card leaves the list when it ends. */
const WIPE_MS = 240;

const session = {
  get<T>(k: string): T | null { try { const v = sessionStorage.getItem(k); return v ? (JSON.parse(v) as T) : null; } catch { return null; } },
  set(k: string, v: unknown) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch { /* a private window; nothing is lost but the scroll */ } },
  del(k: string) { try { sessionStorage.removeItem(k); } catch { /* ditto */ } },
};

/**
 * The count the score bug shows: the person's decisions that took an item out of the ten today, on any device (BUGS.md T15;
 * it was this browser's own, in localStorage, so a laptop and a phone said different numbers). It is read from the server's
 * list of what they cleared (GET /api/triage/cleared, every store on the page, undone ones left out), counted from midnight
 * where the page is open; a press made since the last read is added on the page until the next read has it.
 */
export function clearedToday(decidedAt: readonly string[], now: Date = new Date()): number {
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return decidedAt.filter((at) => { const t = Date.parse(at); return Number.isFinite(t) && t >= midnight; }).length;
}

/** The count over the stores, or null when no store's list could be read (the count then stays as it was). */
async function readClearedToday(stores: Store[]): Promise<number | null> {
  const reads = await Promise.all(stores.map((s) => triage.cleared(s.tenantId).then((r) => r.items.map((i) => i.decidedAt), () => null)));
  const got = reads.filter((r): r is string[] => r !== null);
  return got.length === 0 ? null : clearedToday(got.flat());
}

/** The draft purpose an option asks for (contract: kind draft, reply or chase with params.purpose). */
export function purposeOf(o: Option): DraftIntent['purpose'] {
  const p = o.params && typeof o.params.purpose === 'string' ? o.params.purpose : null;
  if (p === 'reply' || p === 'chase' || p === 'acknowledge' || p === 'invite' || p === 'note') return p;
  return o.kind === 'chase' ? 'chase' : 'reply';
}

async function loadStores(stores: Store[]): Promise<Loaded> {
  const [views, clearedNow] = await Promise.all([Promise.all(stores.map((s) => triage.list(s.tenantId))), readClearedToday(stores)]);
  const lists = views.map((v, i) => v.items.map((it) => ({ ...it, store: it.store || stores[i]!.tenantId, private: stores[i]!.kind === 'personal' })));
  const team: Record<string, TeamMember[]> = {};
  views.forEach((v, i) => { team[stores[i]!.tenantId] = v.team; });
  const tops: TopOfPass[] = views.map((v) => v as unknown as TopOfPass);
  // Module 04 D299 answer 1: each store's own cards its calls cleared, newest first, the stores in the page's order.
  const cleared = views.flatMap((v, i) => clearedOf(v).map((c) => ({ ...c, store: stores[i]!.tenantId })));
  // The blended page is one list: it holds as many as the larger store's pass says, and is topped up only when no store's was chosen.
  // Module 04 B2220, BUGS.md T55: each store's still-open count, as one over the stores on the page.
  const stillOpen = mergeStillOpen(views.map((v, i) => ({ store: stores[i]!.tenantId, stillOpen: stillOpenOf(v) })));
  const passes = views.map((v, i) => ({ store: stores[i]!.tenantId, passId: v.passId }));
  return { items: mergeStores(lists), team, ranked: views.some((v) => v.passId !== null), at: Date.now(), top: Math.max(TEN, ...tops.map((v) => v.top ?? TEN)), chosen: tops.some((v) => v.chosen === true), cleared, clearedToday: clearedNow, stillOpen, passes };
}

// ---- Module 04 D288, D299 answer 1 (REQ-2026-09-24-09): the cards a call cleared, above Your list ----

/** The words a line takes once Reopen has gone through (D299 answer 1: "A Reopen button puts the card back within fifteen minutes"). */
export const REOPENED = 'Reopened. It will be back on your list within fifteen minutes.';

/**
 * A cleared card's line, in the words Mike chose (D299 answer 1): "Cleared after your call with Alex Roberts: “We'll go with
 * twelve months.”" The quote is the call summary's own words; with none, Cobrain's own line stands after the colon. Null when
 * the read names nobody or says nothing, which it never writes: such a line is left out rather than drawn with a gap.
 */
export function clearedWords(c: Pick<ClearedCard, 'callee' | 'quote' | 'line'>): string | null {
  const callee = c.callee?.trim();
  if (!callee) return null;
  const quote = c.quote?.trim();
  if (quote) return `Cleared after your call with ${callee}: “${quote}”`;
  const line = c.line?.trim();
  return line ? `Cleared after your call with ${callee}: ${line}` : null;
}

const lineKey = (l: Pick<ClearedLine, 'store' | 'judgementId'>): string => `${l.store}:${l.judgementId}`;

/**
 * The lines he reopened on this page, kept until the page reloads: once a card is reopened the server stops listing it, and a
 * re-read must not take "Reopened" away from under him. Module state, as the list's own cache is, so it outlives a visit to an
 * item and goes with a reload.
 */
const reopenedLines = new Map<string, ClearedLine>();

/**
 * The lines a fresh read draws: the server's, in its order, and each line he reopened that it no longer lists kept where it
 * stood in `prev`, the last list drawn for these stores (before the first line that followed it and is still drawn). A line
 * reopened under another set of stores, still on the page, goes last. Pure.
 */
export function keepReopened(prev: readonly ClearedLine[], fresh: readonly ClearedLine[], reopened: ReadonlyMap<string, ClearedLine>, stores: ReadonlySet<string>): ClearedLine[] {
  const out = [...fresh];
  const at = (k: string): number => out.findIndex((o) => lineKey(o) === k);
  prev.forEach((p, i) => {
    const k = lineKey(p);
    if (!reopened.has(k) || !stores.has(p.store) || at(k) >= 0) return;
    const next = prev.slice(i + 1).map(lineKey).find((n) => at(n) >= 0);
    out.splice(next === undefined ? out.length : at(next), 0, p);
  });
  for (const [k, l] of reopened) if (stores.has(l.store) && at(k) < 0) out.push(l);
  return out;
}

/** One cleared line and its Reopen. A refusal is the server's own words, under the line, which stays as it was. */
function ClearedRow({ line, reopened, onReopened }: { line: ClearedLine; reopened: boolean; onReopened: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reopen = async () => {
    setBusy(true); setError(null);
    try {
      await reopenCallRead(line.store, line.judgementId);
      onReopened();
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <p title={line.headline ?? undefined} data-cleared={line.judgementId}>
        {reopened ? REOPENED : <>{clearedWords(line)}{' '}<ActionButton type="button" className="link inline" disabled={busy} onClick={() => void reopen()}>Reopen</ActionButton></>}
      </p>
      {error && !reopened ? <p className="error" role="alert">{error}</p> : null}
    </>
  );
}

/**
 * Above Your list (D299 answer 1): one line for each card a call he placed cleared in the last two days, each with Reopen, the
 * card's headline its title so he can tell which card it was. Nothing at all is drawn when no call cleared a card, or the
 * server is older than the read. The margin keeps the line clear of the score bug's ground strip, as the banner's does.
 */
function ClearedByCall({ lines }: { lines: readonly ClearedLine[] }) {
  const [, setDrawn] = useState(0);
  const shown = lines.filter((l) => reopenedLines.has(lineKey(l)) || clearedWords(l) !== null);
  if (shown.length === 0) return null;
  return (
    <div className="aware" role="note" style={{ margin: '0 0 12px' }}>
      {shown.map((l) => (
        <ClearedRow key={lineKey(l)} line={l} reopened={reopenedLines.has(lineKey(l))}
                    onReopened={() => { reopenedLines.set(lineKey(l), l); setDrawn((n) => n + 1); }} />
      ))}
    </div>
  );
}

// ---- Module 04 B2220, BUGS.md T55: under the list, what is still open and not today ----

/** The line under the list, in Mike's words (T54 (2), T55): "Still open, not today: 236, longest waiting: 12 days". */
export function stillOpenLine(s: Pick<StillOpen, 'count' | 'longestDays'>): string {
  return `Still open, not today: ${s.count.toLocaleString('en-GB')}, longest waiting: ${count(s.longestDays, 'day')}`;
}

/**
 * The line and the full open list it opens. Each entry is the item's headline and when it was last in front of the person,
 * in the card's own words, longest waiting first; an entry whose card the page holds (below the list) opens it. The read
 * names at most two hundred: past them the list says how many more there are. Nothing is drawn when nothing is open beyond
 * the list, or the read carries no count.
 */
function StillOpenList({ still, items, openItem }: { still: StillOpenView | null; items: PassItem[]; openItem: (it: PassItem) => void }) {
  const [open, setOpen] = useState(false);
  if (!still || still.count <= 0) return null;
  const held = new Map<string, PassItem>(items.map((it) => [`${it.store}:${it.subjectKind}:${it.subjectId}`, it]));
  const more = still.count - still.items.length;
  return (
    <div className="still-open">
      <p className="note"><ActionButton reading type="button" className="link inline" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{stillOpenLine(still)}</ActionButton></p>
      {open ? (
        <ul className="still-open-list">
          {still.items.map((x) => {
            const it = held.get(`${x.store}:${x.kind}:${x.id}`);
            return (
              <li key={`${x.store}:${x.kind}:${x.id}`} data-still-open={x.id}>
                <strong>{x.headline}</strong> <span className="back-since">{sinceWords(x.lastOn)}</span>
                {it ? <>{' '}<ActionButton reading type="button" className="link inline" onClick={() => openItem(it)}>Open</ActionButton></> : null}
              </li>
            );
          })}
          {more > 0 ? <li className="note">and {more.toLocaleString('en-GB')} more</li> : null}
        </ul>
      ) : null}
    </div>
  );
}

/** The next fixed time among the items on the page: the soonest clock still to come (the bug's third segment). */
function nextFixed(items: PassItem[], now: number): { text: string; today: boolean } | null {
  let best: PassItem | null = null;
  for (const it of items) {
    const at = it.when?.at ? Date.parse(it.when.at) : NaN;
    if (!Number.isFinite(at) || at < now) continue;
    if (!best || at < Date.parse(best.when!.at!)) best = it;
  }
  if (!best) return null;
  const at = new Date(best.when!.at!);
  const today = at.toDateString() === new Date(now).toDateString();
  return { text: whenText(best.when) ?? '', today };
}

/** A decision held for undo (U34). */
interface Held { item: PassItem; body: Omit<DecideBody, 'store'>; words: string; rose: string | null; roseId: string | null; timer: number; undone?: boolean }
interface Refused { message: string; body: Omit<DecideBody, 'store'>; words: string; passing: boolean }

const typing = (t: EventTarget | null): boolean => {
  const el = t as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};
const reduced = (): boolean => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };

const KEYS: Array<[string, string]> = [
  ['J or ↓, K or ↑', 'Move to the next or previous item'],
  ['1 to 4', 'Choose that option on the current item'],
  ['Enter', 'Open the current item'],
  ['C', 'Complete'], ['D', 'Dismiss'], ['P', 'Park until…'], ['G', 'Delegate'], ['W', 'Why is this here?'], ['T', 'Teach Cobrain'],
  ['Z or Esc', 'Undo, while the strip shows'],
  ['⌘K or /', 'Ask anything'],
  ['?', 'Show or hide these keys'],
];
const STANDING_KEYS: Record<string, Standing> = { c: 'complete', d: 'dismiss', p: 'park', g: 'delegate', w: 'why' };

export function Triage({ stores, questions, go }: { stores: Store[]; questions: Questions; go: (path: string) => void }) {
  const readingBack = useReadingBack(stores);
  const key = stores.map((s) => s.tenantId).join('+');
  const [load, setLoad] = useState<Load>(() => { const c = cache.get(key); return c && Date.now() - c.at < CACHE_MS ? { kind: 'ready', data: c } : { kind: 'loading' }; });
  const [askState, setAskState] = useState<AskState>(askStart);
  const [showAll, setShowAll] = useState(false);
  // Folded on load (U31: the ten starts on the first screen; supersedes B191's expanded default). The pill in the bar opens it.
  const [askedOpen, setAskedOpen] = useState(false);
  const [notes, setNotes] = useState<Notification[]>([]);
  const [handled, setHandled] = useState<Set<string>>(() => new Set(session.get<string[]>(HANDLED_KEY) ?? []));
  const [tick, setTick] = useState(0);
  const restored = useRef(false);
  // The question count in the bar opens the card it points at.
  useEffect(() => { const on = () => setAskedOpen(true); window.addEventListener('cobrain:questions', on); return () => window.removeEventListener('cobrain:questions', on); }, []);

  // The upgrade's state: the keyboard's current item, the held decision, the motion marks, the count cleared today.
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [held, setHeld] = useState<Held | null>(null);
  const heldRef = useRef<Held | null>(null);
  const [leaving, setLeaving] = useState<string | null>(null);
  const [arriving, setArriving] = useState<Set<string>>(new Set());
  const [moved, setMoved] = useState<Set<string>>(new Set());
  const [heldErrors, setHeldErrors] = useState<Record<string, Refused>>({});
  const [cleared, setCleared] = useState(() => cache.get(key)?.clearedToday ?? 0);
  const [flip, setFlip] = useState(0);
  const [keysOpen, setKeysOpen] = useState(false);
  const [clearedOpen, setClearedOpen] = useState(false);
  const [historyRevision, setHistoryRevision] = useState(0);
  const [f, setF] = useFilterParams(FIND);
  const handles = useRef(new Map<string, CardHandle>());
  // Playbooks K7 (U274): an approver's cards for what waits to be placed, read from the commercial store, and what a press
  // on one says in the strip, with its undo. They follow the ranked list (Cowork's reading of U274: never above a
  // client's own item), at its bottom since Quick wins went (REQ-2026-10-13-16, Mike by pop-up, "Compete for the list"). A store question's personal store holds no playbook.
  const playbookStore = stores.find((x) => x.kind === 'commercial')?.tenantId ?? null;
  const alloc = useToAllocate(playbookStore);
  // BUGS.md T65: a block booked from a card says so here too, with its Undo; `failed` is what a refused Undo adds.
  const [said, setSaid] = useState<(AllocateSaid & { failed?: string }) | null>(null);
  // Mike, by pop-up, 5 Oct about 19:20 CEST: "First three, then Show all (Recommended)", as the Awareness strip folds.
  const [allocAll, setAllocAll] = useState(false);
  const saidTimer = useRef(0);

  // Module 04 B2220, BUGS.md T55 (migration 226): a read that draws the list for him, as the page opens (`load`) or as he comes
  // back to it (`focus`), says so once it has landed, naming the pass each store's list was drawn from. The page's own re-read
  // (a version chosen) says nothing; Try again after a read that drew nothing is the page opening, so it is a load.
  // The page reads its list again whenever the stores it is handed are a new array (every render of App), and each such read
  // asks as a load: only the first read of a mount that lands says so, per set of stores; a read that churn asks for says nothing.
  const loadSaid = useRef<string | null>(null);
  const refresh = useCallback((via: VisitVia | null = null) => {
    let cancelled = false;
    loadStores(stores)
      .then((fresh) => {
        if (cancelled) return;
        // D299 answer 1: a line he reopened stays where it was until the page reloads, though the server lists it no more.
        const data = { ...fresh, cleared: keepReopened(cache.get(key)?.cleared ?? [], fresh.cleared, reopenedLines, new Set(stores.map((s) => s.tenantId))) };
        cache.set(key, data); setLoad({ kind: 'ready', data });
        const says = via === 'load' && loadSaid.current === key ? null : via;
        if (says === 'load') loadSaid.current = key;
        if (says) for (const p of fresh.passes) recordVisit(p.store, p.passId, says);
        // T15: the server's count, and the decision still held on the page, which it cannot have yet.
        if (fresh.clearedToday !== null) setCleared(fresh.clearedToday + (heldRef.current ? 1 : 0));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        // A ten that is on the screen stays there: now that every mount reads through the cache,
        // a read that does not land must not replace a working list with an error.
        setLoad((l) => (l.kind === 'ready' ? l : { kind: 'error', message: e instanceof Error ? e.message : String(e) }));
      });
    return () => { cancelled = true; };
  }, [key, stores]);

  // The kept list goes up at once so Back from an item returns to the same place (U12), and the
  // store is read underneath it either way (B1261). It used to be a GATE: within ten minutes of
  // the last read the cache was shown and nothing was asked, so clicking Triage -> an item ->
  // Back, or Tasks -> Triage, showed a list up to ten minutes old, and the only thing that could
  // clear it was reloading the browser -- which drops this module and its cache with it. That is
  // the half of Mike's 2026-09-21 report that the attention policy does not reach: coming back to
  // a page INSIDE the app raises no focus and no visibilitychange, because the window never left.
  // The list is stale the moment he looks away: a decision taken on his phone, a mail that arrived.
  // refresh() hands back its own canceller rather than a promise, so the live re-read holds it and
  // cancels the read before it. Two answers in the air could otherwise land oldest last, putting the
  // ten he has already worked through back on the page. EVERY read of the ten goes through this,
  // the one on mount included: they are the same two answers in the air if he comes back to the
  // window in the moment he opens the page, and one canceller has to cover both.
  const cancelLive = useRef<(() => void) | null>(null);
  const live = useCallback((via: VisitVia | null = null) => { cancelLive.current?.(); cancelLive.current = refresh(via); }, [refresh]);
  useLiveRefresh(() => live('focus'));

  useEffect(() => {
    const c = cache.get(key);
    setLoad(c && Date.now() - c.at < CACHE_MS ? { kind: 'ready', data: c } : { kind: 'loading' });
    live('load');
    return () => { cancelLive.current?.(); cancelLive.current = null; };
  }, [key, live]);

  // U12: back to the same place in the ten.
  useEffect(() => {
    if (load.kind !== 'ready' || restored.current) return;
    restored.current = true;
    const y = session.get<number>(SCROLL_KEY);
    if (y) { window.scrollTo(0, y); session.del(SCROLL_KEY); }
  }, [load.kind]);

  // The banner: platform.notification rows, polled every minute (D12, D46; milestone 4 check 5).
  useEffect(() => {
    let cancelled = false;
    const poll = () => {
      Promise.all(stores.map((s) => triage.notifications(s.tenantId).then((r) => r.notifications.map((n) => ({ ...n, private: s.kind === 'personal' }))).catch(() => [] as Notification[])))
        .then((lists) => { if (!cancelled) setNotes(lists.flat()); });
    };
    poll();
    const t = window.setInterval(() => { poll(); setTick((x) => x + 1); }, 60_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, [stores]);

  const topNow = load.kind === 'ready' ? load.data.top : TEN;
  const chosenNow = load.kind === 'ready' ? load.data.chosen : false;
  const tenIds = (items: PassItem[]) => stacks(items, topNow, chosenNow).ten.map((i) => i.id);
  const update = (fn: (items: PassItem[]) => PassItem[]) => {
    setLoad((l) => {
      if (l.kind !== 'ready') return l;
      // U2: what is at eleven moves up as these clear, so the ranks are re-numbered after every change (the order is kept).
      const data = { ...l.data, items: mergeStores([fn(l.data.items)]) };
      cache.set(key, data);
      return { kind: 'ready', data };
    });
  };
  // U28's motion marks: after a change, an item new to the ten arrives and an item whose rank changed moves.
  const ranksRef = useRef<Map<string, number> | null>(null);
  const loadedItems = load.kind === 'ready' ? load.data.items : null;
  useEffect(() => {
    if (!loadedItems) return;
    const after = stacks(loadedItems, topNow, chosenNow).ten;
    const before = ranksRef.current;
    ranksRef.current = new Map(after.map((i) => [i.id, i.rankNo]));
    if (!before) return;   // the first draw is not a change
    setArriving(new Set(after.filter((i) => !before.has(i.id)).map((i) => i.id)));
    setMoved(new Set(after.filter((i) => before.has(i.id) && before.get(i.id) !== i.rankNo).map((i) => i.id)));
  }, [loadedItems]);
  const onGone = (id: string) => { update((items) => items.filter((i) => i.id !== id)); setHistoryRevision((value) => value + 1); };
  const onUpdate = (item: PassItem) => update((items) => items.map((i) => (i.id === item.id ? { ...item, private: i.private, store: i.store } : i)));
  const openItem = (item: PassItem) => { session.set(SCROLL_KEY, window.scrollY); go(`/items/${encodeURIComponent(item.id)}?store=${encodeURIComponent(item.store)}`); };
  const onDraft = (item: PassItem, o: Option, r: DecideResult | null, instruction: string | null = null) => {
    const intent: DraftIntent = {
      itemId: item.id, store: item.store, draftId: r?.draft?.id ?? null, purpose: purposeOf(o),
      language: o.params && typeof o.params.language === 'string' ? o.params.language : null,
      instruction, optionKey: o.key,
    };
    session.set(DRAFT_KEY, intent);
    openItem(item);
  };
  const onNotPrivate = (item: PassItem) => {
    // D77: a wrong call is one click to fix. The label is D83's X, wrong store; the move itself is the store's.
    void triage.decide(item.id, { store: item.store, action: 'other', label: 'wrong_store', note: 'not_private' }).then(() => onGone(item.id)).catch(() => undefined);
  };
  const handle = (n: Notification, action: 'on_my_way' | 'snooze') => {
    const next = new Set(handled); next.add(n.id); setHandled(next); session.set(HANDLED_KEY, [...next]);
    if (!n.passItemId) return;
    const store = stores.find((s) => (s.kind === 'personal') === n.private)?.tenantId ?? stores[0]!.tenantId;
    const body = action === 'snooze'
      ? { store, action: 'park' as const, until: new Date(Date.now() + 15 * 60_000).toISOString() }
      : { store, action: 'other' as const, note: 'on_my_way' };
    void triage.decide(n.passItemId, body).catch(() => undefined);
  };
  const open = (path: string) => { if (path.startsWith('/items/')) session.set(SCROLL_KEY, window.scrollY); go(path); };

  // ---- U34: hold, send, undo ----
  const bump = (by: number) => setCleared((c) => Math.max(0, c + by));
  const send = (h: Held) => {
    window.clearTimeout(h.timer);
    triage.decide(h.item.id, { store: h.item.store, ...h.body })
      .then(() => setHistoryRevision((value) => value + 1))
      .catch((e: unknown) => {
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        // The decision did not land: the item comes back to its place and says why, in the server's words.
        const message = e instanceof ApiError || e instanceof Error ? e.message : String(e);
        // A refusal (4xx) says why and offers the item; only a passing failure (the network, a 5xx) offers to send it again.
        const passing = !(e instanceof ApiError) || e.status >= 500;
        setHeldErrors((m) => ({ ...m, [h.item.id]: { message: `That did not go through: ${message.replace(/\.$/, '')}.`, body: h.body, words: h.words, passing } }));
        bump(-1);
        setCurrentId(h.item.id);   // the marker goes back to the card that holds the reason
        update((items) => (items.some((i) => i.id === h.item.id) ? items : [...items, h.item]));
      });
  };
  const settle = () => { const h = heldRef.current; heldRef.current = null; setHeld(null); if (h) send(h); };
  const hold = (item: PassItem, body: Omit<DecideBody, 'store'>, words: string) => {
    if (heldRef.current) settle();   // a second decision sends the first
    setHeldErrors((m) => { const { [item.id]: _, ...rest } = m; return rest; });
    const items = load.kind === 'ready' ? load.data.items : [];
    const before = new Set(tenIds(items));
    const rest = items.filter((i) => i.id !== item.id);
    const afterTen = stacks(mergeStores([rest]), topNow, chosenNow).ten;
    const rose = afterTen.find((i) => !before.has(i.id)) ?? null;
    // U2: the strip says what moved up to ten, or, when nothing was waiting at eleven, how many are left.
    const h: Held = { item, body, words, rose: rose ? `Moved up onto your list: ${rose.headline}` : afterTen.length === 0 ? 'That’s your list.' : `${afterTen.length} left on your list.`, roseId: rose?.id ?? null, timer: 0 };
    h.timer = window.setTimeout(() => { if (heldRef.current === h) settle(); }, HOLD_MS);
    heldRef.current = h;
    setHeld(h);
    bump(1); setFlip((f) => f + 1);
    window.clearTimeout(saidTimer.current); setSaid(null);   // the strip says the newest press; an allocation's undo stays under Allocated on Playbooks
    // Keep the keyboard on the same place in the list: the item that takes this one's place becomes current.
    if (currentId === item.id) { const list = navList(); const at = list.findIndex((i) => i.id === item.id); setCurrentId(list[at + 1]?.id ?? list[at - 1]?.id ?? null); }
    if (reduced()) { onGone(item.id); return; }
    setLeaving(item.id);
    // An Undo pressed inside the wipe has put the card back already: the wipe ends without taking it away again.
    window.setTimeout(() => { setLeaving((l) => (l === item.id ? null : l)); if (!h.undone) onGone(item.id); }, WIPE_MS);
  };
  const pause = () => { const h = heldRef.current; if (h) window.clearTimeout(h.timer); window.clearTimeout(saidTimer.current); };
  const resume = () => {
    window.clearTimeout(saidTimer.current);
    if (said) saidTimer.current = window.setTimeout(() => setSaid(null), HOLD_MS);
    const h = heldRef.current; if (!h) return; window.clearTimeout(h.timer); h.timer = window.setTimeout(() => { if (heldRef.current === h) settle(); }, HOLD_MS);
  };
  // K7: a press on an allocation card has been made already (the tab's route and its undo, D62), so the strip says what was
  // done and Undo asks the server to put it back; it holds for as long as a triage decision does.
  const sayAllocated = (x: AllocateSaid & { failed?: string }) => {
    if (heldRef.current) settle();   // a second decision sends the first, as on any card
    window.clearTimeout(saidTimer.current);
    setSaid(x);
    saidTimer.current = window.setTimeout(() => setSaid(null), HOLD_MS);
  };
  const undoAllocated = () => {
    const x = said;
    if (!x?.undo) return;
    window.clearTimeout(saidTimer.current);
    setSaid(null);
    x.undo().catch((e: unknown) => {
      const message = e instanceof Error ? e.message : String(e);
      setSaid({ words: `Not undone: ${message.replace(/\.$/, '')}. ${x.failed ?? 'It is under Allocated on Playbooks.'}`, undo: null });
      saidTimer.current = window.setTimeout(() => setSaid(null), HOLD_MS);
    });
  };
  const undo = () => {
    const h = heldRef.current;
    if (!h) return;
    window.clearTimeout(h.timer);
    h.undone = true;
    heldRef.current = null; setHeld(null);
    setLeaving((l) => (l === h.item.id ? null : l));
    bump(-1);
    update((items) => (items.some((i) => i.id === h.item.id) ? items : [...items, h.item]));
    setCurrentId(h.item.id);
  };
  // Leaving the page sends what is held (U34): an unmount inside the app, or the tab closing.
  useEffect(() => {
    const leave = () => { const h = heldRef.current; if (!h) return; heldRef.current = null; window.clearTimeout(h.timer); triage.decideOnLeave(h.item.id, { store: h.item.store, ...h.body }); };
    window.addEventListener('pagehide', leave);
    return () => { window.removeEventListener('pagehide', leave); leave(); };
  }, []);

  const self = (item: PassItem) => stores.find((s) => s.tenantId === item.store)?.principalId ?? '';
  const teamFor = (item: PassItem) => (load.kind === 'ready' ? load.data.team[item.store] ?? [] : []);
  const register = useCallback((id: string, h: CardHandle | null) => { if (h) handles.current.set(id, h); else handles.current.delete(id); }, []);
  const card = (item: PassItem, compact = false, rank = true) => (
    <ItemCard key={item.id} foundReadingBack={foundReadingBack(item, readingBack[item.store]?.plan)} item={item} team={teamFor(item)} self={self(item)} compact={compact} rank={rank} open={open}
              onGone={onGone} onUpdate={onUpdate} onDraft={onDraft} onNotPrivate={onNotPrivate}
              hold={hold} say={sayAllocated} numbers={rank} current={rank && currentId === item.id} {...(rank ? { onRegister: register } : {})}
              heldError={heldErrors[item.id]?.message ?? null}
              teach
              {...(heldErrors[item.id]?.passing ? { onRetry: () => { const r = heldErrors[item.id]!; hold(item, r.body, r.words); } } : {})}
              motion={leaving === item.id ? 'leaving' : arriving.has(item.id) ? 'arriving' : moved.has(item.id) ? 'moved' : null} />
  );

  const banner = imminent(notes, handled, Date.now() + tick * 0)[0] as Notification | undefined;
  const dim = askState.result || askState.busy ? ' dimmed' : '';
  const data = load.kind === 'ready' ? load.data : null;
  const whole = data ? stacks(data.items, data.top, data.chosen) : null;
  // Pipeline F6: the bar hides cards and never re-ranks, so each stack keeps its order and each card its number.
  const narrowed = FIND.some((k) => f[k] !== '');
  const s = whole && narrowed
    ? { ...whole, ten: whole.ten.filter((it) => fitsNarrowing(it, f)), awareness: whole.awareness.filter((it) => fitsNarrowing(it, f)) }
    : whole;
  const onPage = whole ? [...whole.ten, ...whole.awareness] : [];
  // K7: the cards for what waits answer the find by their words, and no company, deal, channel or kind narrows to one.
  const allocShown = !narrowed ? alloc.items : f.company || f.matter || f.channel || f.kind ? [] : alloc.items.filter((i) => itemMatches(i, f.q));
  const shownCount = s ? s.ten.length + s.awareness.length + allocShown.length : null;
  const named = (pick: string, options: readonly { id: string; name: string }[]) => options.find((o) => o.id === pick)?.name ?? null;
  const companies = tally(onPage, (it) => it.organisation ?? null);
  const matters = tally(onPage, (it) => (it.matter ? { id: it.matter.id, name: it.matter.title } : null));
  const channels = tally(onPage, (it) => it.channels.map((c) => ({ id: kindWord(c), name: kindWord(c) })));
  const kinds = tally(onPage, (it) => ({ id: it.subjectKind, name: kindOfCard(it.subjectKind) }));
  const narrowedTo = [f.q.trim() ? `“${f.q.trim()}”` : null, f.company ? named(f.company, companies) ?? 'a company' : null, f.matter ? named(f.matter, matters) ?? 'a matter' : null,
    f.channel || null, f.kind ? kindOfCard(f.kind) : null].filter((x): x is string => x !== null);
  // U25 state 3: lines naming who has a shared item stand apart from the three-then-show-all strip.
  const theirs = (it: PassItem) => { const c = claimOf(it); return !!c && !c.mine; };
  const claimLines = s ? s.awareness.filter(theirs) : [];
  const plainStrip = s ? s.awareness.filter((it) => !theirs(it)) : [];
  const navList = (): PassItem[] => (s ? [...s.ten] : []);
  const next = whole ? nextFixed(whole.ten, Date.now()) : null;

  // ---- U33: the keys ----
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => undefined);
  keyRef.current = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); document.querySelector<HTMLInputElement>('.askbar input')?.focus(); return; }
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (typing(e.target) || document.querySelector('dialog[open]')) return;
    const k = e.key;
    if (k === '/') { e.preventDefault(); document.querySelector<HTMLInputElement>('.askbar input')?.focus(); return; }
    if (k === '?') { setKeysOpen((v) => !v); return; }
    if ((k === 'z' || k === 'Z' || k === 'Escape') && heldRef.current) { e.preventDefault(); undo(); return; }
    if ((k === 'z' || k === 'Z' || k === 'Escape') && said?.undo) { e.preventDefault(); undoAllocated(); return; }
    if (k === 'Escape') { if (keysOpen) setKeysOpen(false); else setCurrentId(null); return; }
    const list = navList();
    if (list.length === 0) return;
    const at = currentId ? list.findIndex((i) => i.id === currentId) : -1;
    const moveTo = (i: number) => {
      const it = list[Math.max(0, Math.min(list.length - 1, i))]!;
      setCurrentId(it.id);
      const a = document.activeElement as HTMLElement | null;
      if (a && a !== document.body && !a.closest(`[data-item="${CSS.escape(it.id)}"]`)) a.blur();
      window.requestAnimationFrame(() => document.querySelector(`[data-item="${CSS.escape(it.id)}"]`)?.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' }));
    };
    if (k === 'j' || k === 'ArrowDown') { e.preventDefault(); moveTo(at + 1); return; }
    if (k === 'k' || k === 'ArrowUp') { e.preventDefault(); moveTo(at < 0 ? 0 : at - 1); return; }
    // A first action key with nothing marked marks item 1 and does nothing else: the page answers, and nothing acts unseen.
    if (at < 0) { if (/^[1-9]$/.test(k) || STANDING_KEYS[k.toLowerCase()] || k === 'Enter' || k.toLowerCase() === 't') { e.preventDefault(); moveTo(0); } return; }
    const cur = list[at]!;
    // A key never acts on a card out of sight: the first press brings the marked card back into view.
    const card = document.querySelector(`[data-item="${CSS.escape(cur.id)}"]`);
    const box = card?.getBoundingClientRect();
    if (box && (box.bottom < 110 || box.top > window.innerHeight - 40) && k !== 'Enter') { e.preventDefault(); card!.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' }); return; }
    if (k === 'Enter') { e.preventDefault(); openItem(cur); return; }
    if (/^[1-9]$/.test(k)) { e.preventDefault(); handles.current.get(cur.id)?.choose(Number(k)); return; }
    if (k === 't' || k === 'T') { e.preventDefault(); handles.current.get(cur.id)?.teach(); return; }
    const st = STANDING_KEYS[k.toLowerCase()];
    if (st) { e.preventDefault(); handles.current.get(cur.id)?.standing(st); }
  };
  useEffect(() => {
    // One cursor: focus that lands inside a card of the list makes that card the current one.
    // Only keyboard focus moves the mark: a mouse press that marked the card would add its letter keys, move the row
    // under the pointer and send the click elsewhere (found at 900 wide on a private card).
    const onFocus = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t || !t.matches(':focus-visible')) return;
      const el = t.closest<HTMLElement>('.list > .item[data-tier]');
      setCurrentId(el?.dataset.item ?? null);
    };
    document.addEventListener('focusin', onFocus);
    return () => document.removeEventListener('focusin', onFocus);
  }, []);
  useEffect(() => {
    const on = (e: KeyboardEvent) => keyRef.current(e);
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, []);

  return (
    <div className="triage">
      <AskBar store={stores[0]!} state={askState} onState={setAskState} open={open} />
      <AskAnswer state={askState}>
        {askState.result?.item
          ? card({ ...askState.result.item, private: stores.find((x) => x.tenantId === askState.result!.item!.store)?.kind === 'personal' }, false, false)
          : null}
      </AskAnswer>

      {banner ? (
        <div className={banner.private ? 'banner private-banner' : 'banner'} role="alert" data-notification={banner.id}>
          <span className="when clock"><i className="live" aria-hidden="true" />{banner.at ? inWords(banner.at) : 'Now'}</span>
          <div className="grow">{banner.private ? <Chip className="private"><svg className="lock" viewBox="0 0 12 12" aria-hidden="true"><path d="M3.5 5V3.8a2.5 2.5 0 015 0V5h.5a1 1 0 011 1v4a1 1 0 01-1 1H3a1 1 0 01-1-1V6a1 1 0 011-1zm1 0h3V3.8a1.5 1.5 0 00-3 0z" /></svg>Private, only you</Chip> : null} {bannerBody(banner.body)}</div>
          <ActionButton type="button" className="opt lead" onClick={() => handle(banner, 'on_my_way')}>On my way</ActionButton>
          <ActionButton type="button" className="opt" onClick={() => handle(banner, 'snooze')}>Snooze 15 min</ActionButton>
        </div>
      ) : null}

      {/* Module 04 D299 answer 1: above Your list, the cards his calls cleared, each with Reopen. */}
      {data ? <ClearedByCall lines={data.cleared} /> : null}

      <div className={`list${dim}`}>
        {/* U28: the score bug heads the ten. The count is the decisions cleared today; the plate is the next fixed time. */}
        <div className="bug" role="group" aria-label="Your list">
          <h1 className="bug-title">Your list</h1>
          <ActionButton reading type="button" className="bug-score" aria-live="polite" aria-expanded={clearedOpen} aria-controls="cleared-history" onClick={() => setClearedOpen((open) => !open)}><span className="digit" key={flip}>{load.kind === 'ready' ? cleared : '–'}</span><span className="bug-words">cleared today</span></ActionButton>
          <div className="bug-next">{load.kind === 'error' ? <span className="muted">Clock unavailable</span> : load.kind === 'loading' ? <span className="muted">Reading your day</span> : next ? <>{next.today ? <i className="live" aria-hidden="true" /> : null}Next: {next.text}</> : 'Nothing fixed ahead'}</div>
          {/* REQ-2026-09-25-06 (D234): which version of Cobrain builds this list, and going back to an earlier one. */}
          <VersionPicker stores={stores} onChosen={() => live(null)} />
          <ActionButton reading type="button" className="bug-keys" onClick={() => setKeysOpen((v) => !v)} aria-expanded={keysOpen}>Keys <kbd>?</kbd></ActionButton>
        </div>
        {clearedOpen ? <ClearedHistory key={key} stores={stores} revision={historyRevision} onUndone={() => live(null)} /> : null}
        {stores.map(s => <ReadingBackStrip key={s.tenantId} value={readingBack[s.tenantId]?.reading ?? null} />)}
        {/* U54, U59: Company intelligence's three lines, beside Outlook and on a phone (intel.css hides them on a wide window). */}
        <CompanyLines go={go} />
        {keysOpen ? (
          <div className="keys-scrim" onClick={(e) => { if (e.target === e.currentTarget) setKeysOpen(false); }}>
          <div className="keys" role="dialog" aria-modal="true" aria-labelledby="keys-title">
            <div className="keys-head"><h2 id="keys-title">Keys</h2><ActionButton reading type="button" className="link inline" onClick={() => setKeysOpen(false)}>Close <kbd>Esc</kbd></ActionButton></div>
            <p className="note keys-start">Press <kbd>J</kbd> to start at item 1; nothing acts until an item is marked.</p>
            <dl>{KEYS.map(([k, w]) => <div key={k}><dt>{k.split(/( or |, | to )/).map((part, i) => /^( or |, | to )$/.test(part) ? <span key={i}>{part}</span> : <kbd key={i}>{part}</kbd>)}</dt><dd>{w}</dd></div>)}</dl>
            <p className="note">No key sends anything: a reply opens its draft, and Send is always a click. The question cards and the banner are answered with the mouse or Tab.</p>
          </div>
          </div>
        ) : null}
        {questions.left > 0 || questions.waiting > 0 ? <QuestionsCard questions={questions} expanded={askedOpen} onToggle={() => setAskedOpen((v) => !v)} /> : null}
        {/* BUGS.md M46: the calls Cobrain holds for their talk of pay or a person's job, asked of their organiser once. */}
        <HeldCallsCard go={go} />
        {/* Milestone 5 check 1's mount (B231; R30, B199), the one line its own track could not
            write because this file is the portal track's. The card asks nothing and draws
            nothing when the API returns no store questions, so it costs an empty list per load
            and never an empty box. It sits above the ten with the tier questions because it is
            the same kind of thing: something Cobrain needs answered before the list is honest. */}
        {/* U39 (Mike, 2026-09-19): the private-store question shows only while Personal is on, as U3 hides every private
            item with the toggle off (Personal off is what a person does before sharing a screen); it waits, unanswered. */}
        {stores.some((x) => x.kind === 'personal') ? <StoreQuestionCard initiallyOpen={false} /> : null}
        <FilterBar find="Find a card" q={f.q} onFind={(q) => setF({ q })} shown={shownCount} total={whole ? onPage.length + alloc.items.length : null} noun={['card', 'cards']}
          onClearAll={() => setF({ q: '', company: '', matter: '', channel: '', kind: '' })}
          picks={[
            { label: 'By company', find: 'Find a company', any: 'Any company', value: f.company, options: whole ? companies : null, onChange: (company) => setF({ company }) },
            { label: 'By deal or matter', find: 'Find a deal or matter', any: 'Any deal or matter', value: f.matter, options: whole ? matters : null, onChange: (matter) => setF({ matter }) },
            { label: 'By channel', find: 'Find a channel', any: 'Any channel', value: f.channel, options: whole ? channels : null, onChange: (channel) => setF({ channel }) },
            { label: 'By kind', find: 'Find a kind', any: 'Any kind', value: f.kind, options: whole ? kinds : null, onChange: (kind) => setF({ kind }) },
          ]} />
        {load.kind === 'loading' ? <div className="skeleton" aria-label="Ranking what matters"><i /><i /><i /></div> : null}
        {load.kind === 'error' ? <div className="error" role="alert">Cobrain could not read your list just now: {load.message.replace(/\.$/, '')}. <ActionButton reading type="button" className="link inline" onClick={() => { setLoad({ kind: 'loading' }); refresh('load'); }}>Try again</ActionButton></div> : null}
        {s && data && !data.ranked ? <p className="plain pad">Cobrain has not ranked your items yet. The first pass runs after the backfill; until then the questions above are what it needs from you.</p> : null}
        {whole && narrowed && whole.ten.length > 0 ? <p className="note narrowed-line" role="status">{narrowedLine(s!.ten.length, whole.ten.length, narrowedTo)}</p> : null}
        {s && data && data.ranked && whole!.ten.length === 0 && !held ? <p className="ten-done">That&rsquo;s your list. <span className="note">{allocShown.length > 0 ? 'What waits to be placed is below.' : 'Cobrain raises things as they come.'}</span></p> : null}
        {s ? s.ten.map((it) => card(it)) : null}
        {/* K7: the cards for what waits to be placed, at the bottom of the list (REQ-2026-10-13-16). */}
        {s && allocShown.length > 0 ? (
          <div className="alloc-list">
            {alloc.book && playbookStore ? (allocAll || narrowed ? allocShown : allocShown.slice(0, 3)).map((i) => (
              <AllocateCard key={i.key} store={playbookStore} book={alloc.book!} i={i} open={open}
                onSaid={sayAllocated} onPlaced={alloc.hide} onBack={async (id) => { alloc.show(id); await alloc.reload(); }} />
            )) : null}
            {alloc.book && !narrowed && allocShown.length > 3 ? (
              <p className="note alloc-more"><ActionButton reading type="button" className="link inline" aria-expanded={allocAll} onClick={() => setAllocAll((v) => !v)}>{allocAll ? 'Show the first three' : `Show all ${allocShown.length}`}</ActionButton></p>
            ) : null}
          </div>
        ) : null}
        {/* BUGS.md T46: a list Cobrain chose is never topped up, so under it nothing "moves up"; Mike by pop-up, 8 Oct 2026. */}
        {s && !narrowed && s.overflow.length > 0 ? <p className="note pad">{chosenNow ? `${count(s.overflow.length, 'more thing')} Cobrain left off today's list.` : <>{count(s.overflow.length, 'more thing')} behind these; the next moves up as one clears.</>}</p> : null}
        {/* Module 04 B2220, BUGS.md T55: nothing open drops; what is not on today's list is counted under it. */}
        {data && s ? <StillOpenList still={data.stillOpen} items={data.items} openItem={openItem} /> : null}

        {s && s.awareness.length > 0 ? (
          <>
            <SectionHeading title={<>Awareness</>} note={<>Nothing is asked of you.</>} />
            <div className="aware">
              {/* U25 state 3: a line naming who has a shared item is always shown, above the three. */}
              {claimLines.map((it) => (
                <ClaimLine key={it.id} item={it} open={openItem} onUpdate={onUpdate} onGone={onGone} />
              ))}
              {(showAll ? plainStrip : plainStrip.slice(0, 3)).map((it) => (
                <p key={it.id} className={it.private ? 'aware-private' : ''}>
                  {it.who ? <strong>{it.who}:</strong> : null} {it.headline}{it.gist ? ` ${it.gist}` : ''}{' '}
                  <ActionButton reading type="button" className="link inline" onClick={() => openItem(it)}>Open</ActionButton>
                </p>
              ))}
              <p className="note">
                {plainStrip.length > 3 ? <><ActionButton reading type="button" className="link inline" onClick={() => setShowAll((v) => !v)}>{showAll ? 'Show the first three' : `Show all ${plainStrip.length}`}</ActionButton>{' '}</> : null}
                Should one of these have asked you something? Open it and say so under “Why is this here?”.
              </p>
            </div>
          </>
        ) : null}
      </div>

      {/* U54: Company intelligence's bar, to the right of the ten on a wide window only (intel.css). */}
      <CompanyBar go={go} />

      {/* U34: the undo strip. It names what moved up to ten, and holds the decision until it goes. */}
      <UndoStrip words={held?.words ?? said?.words} rose={held?.rose}
        {...(held?.roseId ? { onRose: () => document.querySelector(`[data-item="${CSS.escape(held.roseId!)}"]`)?.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' }) } : {})}
        onUndo={held ? undo : said?.undo ? undoAllocated : undefined}
        onMouseEnter={pause} onMouseLeave={resume} onFocus={pause} onBlur={resume} />
    </div>
  );
}

